<template>
  <div class="cart" :class="{ visible: isOpen }" ref="cartRef" v-trap="isOpen ? closeCartDrawer : null" role="dialog"
    aria-modal="true" aria-labelledby="cart-heading">
    <div class="cart__inner">
      <!-- Loading indicator -->
      <div v-if="store.isLoading" class="cart__loading">
        <div class="cart__loading-spinner"></div>
      </div>

      <CartUrgencyTimer />

      <div class="cart__header">
        <h3 class="cart__heading" id="cart-heading">
          Your Cart <span class="cart__heading-count">{{ cartLabel }}</span>
          <button class="cart__heading-trigger" @click="closeCartDrawer" aria-label="Close cart">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z"
                fill="black" />
            </svg>
          </button>
        </h3>

        <CartProgressBar v-if="!configuration.enableThresholds" :itemsSubtotalPrice="itemsSubtotalPrice" />
        <CartProgressBarV2 v-if="configuration.enableThresholds" :itemsSubtotalPrice="itemsSubtotalPrice" />
      </div>

      <DiscountCodeBanner />
      <BfcmOfferBanner
        v-show="(!configuration.saleActive && configuration.bfcmEnaled) || (!isFreeGiftInCart() && itemsSubtotalPrice < 9900 && configuration.bfcmEnaled)" />

      <!-- SMS Subscription -->
      <SmsSubscription />
      <!-- Line Items -->
      <div class="line-items-list">
        <!-- <BfcmGift
          v-if="!isFreeGiftInCart() && itemsSubtotalPrice >= 9900 && configuration.bfcmEnaled && configuration.saleActive"
          :itemsSubtotalPrice="itemsSubtotalPrice" /> -->
        <template v-if="items.length !== 0">
          <LineItem v-for="item in items" :key="`line-item-${item.key}`" :item="item"
            :isOperationActive="isOperationActive" :currencyCode="currencyCode" :totalItems="items.length"
            :isOperationTarget="isOperationTarget(item.key)" :operation="getItemOperation(item.key)"
            @changeQuantityAction="onChangeLineItemQuantityEvent" @changeLineItem="changeLineItem"
            @discountCalculated="discountCalculated" :isLoading="store.isLoading" />
        </template>

        <div v-else class="line-items-list__empty-state">
          <h4 class="line-items-list__empty-state-heading">{{ configuration.emptyCartText }}</h4>
          <a class="line-items-list__empty-state-link" :href="configuration.emptyStateShopNowCollectionUrl">
            {{ configuration.emptyCartButtonText }}
          </a>
        </div>
      </div>
      <!-- Upsell Products -->
      <Upsell v-if="items.length === 0" :items="recommendations.items" :showRecommendedStack="showRecommendedStack"
        :onTouch="onTouch" @addProductAction="onAddProductEvent" />
      <UpsellsCompact v-if="showCompactUpsells && items.length > 0" :items="recommendations.items"
        @addProductAction="onAddProductEvent" />
      <!-- Cart Footer -->
      <form v-if="items.length !== 0" class="cart__footer" method="post" :action="getCheckoutUrl()">
        <div class="cart__footer-message">
          <img src="https://cdn.shopify.com/s/files/1/1103/4864/files/Stars.png?v=1762795753" />
          <p>
            <i>Trusted by 530k+ Customers</i>
          </p>
        </div>
        <h4 class="cart__footer-heading">
          SUBTOTAL <span>{{ getFormattedPrice(itemsSubtotalPrice, configuration.currencySymbol) }}</span>
        </h4>

        <button type="submit" class="cart__footer-submit" :disabled="isDisabledSubmit || isOperationActive"
          @click="onCheckoutClick">
          <span style="display:none">Checkout</span>
          <span class="secure-cheackout__label">Secure Checkout
            <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd"
                d="M9.33333 9.55422V8.20482C9.33333 6.43485 10.7511 5 12.5 5C14.2489 5 15.6667 6.43485 15.6667 8.20482V9.55422H15.8333C17.3061 9.55422 18.5 10.7625 18.5 12.253V16.3012C18.5 17.7917 17.3061 19 15.8333 19H9.16667C7.69391 19 6.5 17.7917 6.5 16.3012V12.253C6.5 10.7625 7.69391 9.55422 9.16667 9.55422H9.33333ZM10.3333 8.20482C10.3333 6.99379 11.3034 6.01205 12.5 6.01205C13.6966 6.01205 14.6667 6.99379 14.6667 8.20482V9.55422H10.3333V8.20482Z"
                fill="white" />
            </svg>
          </span>
        </button>
        <p class="cart__footer-text">
          {{ configuration.footerText }}
        </p>
      </form>
    </div>
  </div>
</template>
<script setup>
import { onMounted, ref, reactive, computed, watch, onUnmounted } from 'vue';
import cartMethods from '../../../bundles/utils/helpers/cart-helper';
import { fetchCollection, fetchProductRecommendations } from '../../client/cart';
import { syncFacebookPromotion, shouldRunSyncFacebookPromotion } from '../../utils/sms';
import { EVENTS } from './constants';
import { getFormattedPrice, isFreeGiftItem } from './helpers';

// Components
import LineItem from './line-item.vue';
import Upsell from './up-sell.vue';
import CartProgressBar from './cart-progress-bar.vue';
import CartProgressBarV2 from './cart-progress-bar-v2.vue';
import SmsSubscription from './sms-subscription.vue';
import { lockBodyScroll, unlockBodyScroll } from '../../helpers/common';
import { safeGet } from '../utils/vue-safety';
import { shouldShowInterstitial } from '../interstitial/interstitial-helper';
import { PRODUCT_WITH_META_FIELDS_QUERY } from '../interstitial/interstitial-api-service';
import { transformProductResponse, productRecommendations } from '../interstitial/interstitial-helper';
import { getPopupInstance } from '../interstitial'
import UpsellsCompact from './upsells-compact.vue';
import BfcmOfferBanner from './bfcm-offer-banner.vue';
import BfcmGift from './bfcm-gift.vue';
import CartUrgencyTimer from './urgency-callout.vue';
import DiscountCodeBanner from './discount-code-banner.vue';
// Configuration options
const configuration = ref({
  currencySymbol: window.BPN?.cart?.currencySymbol || '',
  cartCheckoutUrl: window.BPN?.cart?.cartCheckoutUrl || '',
  footerText: window.BPN?.cart?.footerText || '',
  emptyCartText: window.BPN?.cart?.emptyCartText || 'Your cart is empty',
  emptyStateShopNowCollectionUrl: window.BPN?.cart?.emptyStateShopNowCollectionUrl || '/collections/all',
  emptyCartButtonText: window.BPN?.cart?.emptyCartButtonText || 'Shop Now',
  upsellCollectionHandle: window.BPN?.cart?.upsellCollectionHandle || '',
  enableThresholds: window.BPN?.cart?.enableThresholds || false,
  bfcmEnaled: window.theme?.bfcm?.enabled || false,
  saleActive: window.theme?.bfcm?.saleActive || false,
});

// Refs & reactive state
const cartRef = ref(null);
const isTouchable = ref(false);
const isOpen = ref(false);
const showCompactUpsells = ref(false);
const discounts = ref({});
const operation = ref({
  isPerforming: false,
  key: undefined,
  operation: undefined,
});

const store = reactive({
  isLoading: false,
  cart: {},
});

const recommendations = reactive({
  isLoading: false,
  items: [],
});

const popupRef = getPopupInstance()

// Debounced cart refresh
let loadCartTimer = null;
let lastSkipGiftSync = false;
function queueLoadCart(skipGiftSync = false, delayMs = 300) {
  lastSkipGiftSync = lastSkipGiftSync || !!skipGiftSync;
  if (loadCartTimer) clearTimeout(loadCartTimer);
  return new Promise((resolve) => {
    loadCartTimer = setTimeout(async () => {
      const skip = lastSkipGiftSync;
      lastSkipGiftSync = false;
      loadCartTimer = null;
      try {
        await loadCart(skip);
      } finally {
        resolve();
      }
    }, delayMs);
  });
}

// Computed properties
const items = computed(() => store.cart?.items || []);
const itemsSubtotalPrice = computed(() => {
  const cartItems = safeGet(store, 'cart.items', []);

  return cartItems
    .filter(item => !item.properties || !isFreeGiftItem(item))
    .reduce((total, item) => {
      const discount = safeGet(discounts, item.key, 0);

      let discountedPrice = item.discounted_price;

      if (discount) {
        discountedPrice = Math.round(safeGet(item, 'selling_plan_allocation.compare_at_price', item.price) * (1 - discount / 100));
      }

      return total + discountedPrice * item.quantity;
    }, 0);
});

const currencyCode = computed(() => safeGet(store, 'cart.currency', 'USD'));
const showRecommendedStack = computed(() => itemsSubtotalPrice.value > 0);
const totalItemCount = computed(() => store.cart?.item_count || 0);
const isDisabledSubmit = computed(() => items.value.length === 0);
const cartLabel = computed(() => `(${totalItemCount.value} ${totalItemCount.value === 1 ? 'item' : 'items'})`);
const isOperationActive = computed(() => operation.value.key !== undefined);

// Helper methods
const isOperationTarget = (key) => operation.value.key === key;
const getItemOperation = (key) => operation.value.key === key ? operation.value.operation : undefined;

// Last active element before opening the cart drawer
const lastActiveElement = ref(null);

// Add reference to backdrop element
const backdropElement = ref(null);

// Cart data loading
const loadCart = async (skipGiftSync = false) => {
  try {
    store.isLoading = true;
    store.cart = await cartMethods.get();
    if (store.cart.items.length > 0 && store.cart.items.length < 3) {
      await checkLineItemRecommendations(store.cart.items[store.cart.items.length - 1]);
    } else if (store.cart.items.length >= 3) {
      showCompactUpsells.value = true;
    }
    // if (!skipGiftSync) {
    //   await syncFreeGiftsWithCart();
    // }
    await syncBFCMGift();
  } catch (error) {
    console.error('Error loading cart:', error);
  } finally {
    store.isLoading = false;
  }
};

// Recommendations loading
const loadRecommendations = async () => {
  const RECOMMENDATIONS_LIMIT = 10;
  const items = store.cart?.items || [];
  const uniqueIds = [...new Set(items.map(item => item.product_id))];

  try {
    let results = [];

    if (items.length === 0) {
      results = await fetchCollection(configuration.value.upsellCollectionHandle);
    } else {
      const responses = await Promise.all(
        uniqueIds.map(id => fetchProductRecommendations(id)),
      );

      // Flatten and deduplicate results
      results = Array.from(
        new Map(
          responses
            .flatMap(result => result.products)
            .map(product => [product.id, product]),
        ).values(),
      );
    }

    // Filter out products already in cart
    recommendations.items = results
      .filter(product => !uniqueIds.includes(product.product_id))
      .slice(0, RECOMMENDATIONS_LIMIT);

  } catch (error) {
    console.error('Error loading recommendations:', error);
  }
};

// Event handlers
const closeCartDrawer = () => {
  if (isTouchable.value) return;

  isOpen.value = false;

  // Remove visible class from backdrop
  if (backdropElement.value) {
    backdropElement.value.classList.remove('visible');
  }

  unlockBodyScroll();

  // Return focus to the element that was active before opening the drawer
  if (lastActiveElement.value) {
    setTimeout(() => {
      lastActiveElement.value.focus();
      lastActiveElement.value = null;
    }, 300); // Match the transition duration
  }
};

const onTouch = (flag) => {
  isTouchable.value = flag;
};

const onAddProductEvent = async (productId) => {
  try {
    await cartMethods.post('add', { id: parseInt(productId), quantity: 1 });
    queueLoadCart(false);
    // Scroll to top when adding a product
    if (cartRef.value) {
      cartRef.value.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch (error) {
    console.error('Error adding product:', error);
  }
};

const onChangeLineItemQuantityEvent = async ({ key, operation: operationType, quantity }) => {
  operation.value = {
    isPerforming: true,
    key,
    operation: operationType,
  };

  try {
    await cartMethods.change({
      id: key,
      quantity: quantity,
    });
    queueLoadCart(false);
  } catch (error) {
    console.error('Error changing item quantity:', error);
    queueLoadCart(false);
  } finally {
    operation.value = {
      isPerforming: false,
      key: undefined,
      operation: undefined,
    };
  }
};

const discountCalculated = (payload) => {
  discounts[payload.key] = payload.discount;
};

const changeLineItem = async (payload) => {
  store.isLoading = true;
  operation.value = {
    isPerforming: true,
    key: payload.id,
    operation: 'change-line-item',
  };
  try {
    await cartMethods.change(payload);

    queueLoadCart(false);
    store.isLoading = false;
  } catch (error) {
    console.error('Error changing line item:', error);
    queueLoadCart(false);
  } finally {
    operation.value = {
      isPerforming: false,
      key: undefined,
      operation: undefined,
    };
    store.isLoading = false;
  }
};

// let isSyncing = false;

// Helper functions for modularity
const shouldShowInterstitialPopup = (e, cartItemCount) => {
  return shouldShowInterstitial(e, cartItemCount) &&
    !popupRef.state.isVisible &&
    !isOpen.value &&
    (cartItemCount <= (window.BPN?.interstitial?.max_products || 3));
};

const showInterstitialPopup = async (product) => {
  // isSyncing = true;
  popupRef.open(product);
  // setTimeout(() => {
  //   isSyncing = false;
  // }, 1000);
};

const showBackdrop = () => {
  if (backdropElement.value) {
    backdropElement.value.classList.add('visible');
  } else {
    console.error('Cannot show backdrop - element not found');
  }
};

const focusCloseButton = () => {
  setTimeout(() => {
    const closeButton = cartRef.value?.querySelector('.cart__heading-trigger');
    if (closeButton) {
      closeButton.focus();
    }
  }, 100);
};

const openCartDrawer = async (fetchCart = true) => {
  // Store focused element and show drawer
  lastActiveElement.value = document.activeElement;
  isOpen.value = true;

  // Setup UI elements
  showBackdrop();
  lockBodyScroll();
  focusCloseButton();

  // Handle loading state with delay to avoid flickering
  // const loadingTimer = setTimeout(() => {
  //   store.isLoading = true;
  // }, 200);

  // Load cart data
  if (fetchCart) {
    await loadCart()
  }
  // .finally(() => {
  //   clearTimeout(loadingTimer);
  //   store.isLoading = false;
  // });

  // Reset syncing state
  // setTimeout(() => {
  //   isSyncing = false;
  // }, 1000);
};

const cartEventListener = async (e) => {
  if (popupRef.state.isVisible) {
    popupRef.close()
    // return;
  }
  try {
    // isSyncing = true;
    await queueLoadCart(false);
    const cartItemCount = store.cart.items.length;

    // Check if we should show interstitial popup

    if (shouldShowInterstitialPopup(e, cartItemCount)) {
      const product = store.cart.items[0];
      await showInterstitialPopup(product);
      return;
    }

    // Otherwise, open cart drawer normally
    openCartDrawer(false);

  } catch (error) {
    if (!isOpen.value) {
      openCartDrawer();
    }
  }
};

const onCartUpdateEvent = async (event) => {
  if (event.detail) {
    store.cart = event.detail;
  } else {
    queueLoadCart(false);
  }
};

const handleEscKey = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeCartDrawer();
  }
};

onMounted(async () => {
  backdropElement.value = document.getElementById('cart-backdrop');
  if (backdropElement.value) {
    backdropElement.value.addEventListener('click', closeCartDrawer);
  } else {
    console.error('Backdrop element not found with ID "cart-backdrop"');
  }

  document.addEventListener(EVENTS.OPEN, () => {
    openCartDrawer();
  });

  document.addEventListener(EVENTS.ITEM_ADDED, cartEventListener);

  document.addEventListener(EVENTS.UPDATED, onCartUpdateEvent);
  // document.addEventListener(EVENTS.REBUY_CART_ADD, cartEventListener);
  document.addEventListener('keydown', handleEscKey);

  // Check for openCart param in URL
  const params = new URLSearchParams(window.location.search);
  if (params.get('openCart') === 'true') {
    openCartDrawer();
  }

  window.addEventListener('saleActiveChanged', (e) => {
    configuration.value.saleActive = e.detail.saleActive;
  });

});

onUnmounted(() => {
  // Remove click event from backdrop
  if (backdropElement.value) {
    backdropElement.value.removeEventListener('click', closeCartDrawer);
  }

  // Remove event listeners
  document.removeEventListener(EVENTS.OPEN, cartEventListener);
  document.removeEventListener(EVENTS.UPDATED, onCartUpdateEvent);
  // document.removeEventListener(EVENTS.REBUY_CART_ADD, cartEventListener);
  document.addEventListener(EVENTS.ITEM_ADDED, onCartUpdateEvent);
  document.removeEventListener('keydown', handleEscKey);

  // Ensure body scroll is restored if component unmounts while drawer is open
  if (isOpen.value) {
    unlockBodyScroll();
  }
});

// Watchers
watch(
  () => items.value,
  async () => {
    // Update Facebook promotion if needed
    if (shouldRunSyncFacebookPromotion()) {
      syncFacebookPromotion(items.value);
    }

    // Load product recommendations with a slight delay to prioritize cart display
    if (isOpen.value) {
      setTimeout(() => {
        loadRecommendations();
      }, 500);
    }
  },
  { deep: true },
);

// Update cart count in all header cart count elements
watch(() => totalItemCount.value, () => {
  const cartCountElements = document.querySelectorAll('.cart-count');

  cartCountElements.forEach(element => {
    element.textContent = totalItemCount.value;
  });
});

// Add getCheckoutUrl function
const getCheckoutUrl = () => {
  const baseUrl = configuration.value.cartCheckoutUrl;
  const athleteDiscountCode = window.BPN?.athleteDiscountCode;
  if (athleteDiscountCode) {
    return `${baseUrl}?discount=${athleteDiscountCode}`;
  }
  return baseUrl;
};

// Checks if a cart item is a free gift for a given variant
function isGiftItem(item, variantId) {
  return (
    item.variant_id === variantId &&
    item.properties && item.properties._isFreeGift === true
  );
}

// Returns true if the free gift is in the cart
function isFreeGiftInCart() {
  return store.cart?.items?.some(item => isFreeGiftItem(item));
}

// Add a free gift to the cart
async function addFreeGiftToCart(variantId) {
  await cartMethods.post('add', {
    id: variantId,
    quantity: 1,
    properties: { _isFreeGift: true }
  })
  queueLoadCart(true);
}

let isUpdatingGifts = false;

async function syncFreeGiftsWithCart() {
  store.isLoading = true;
  if (!window.BPN?.cart?.enableThresholds) {
    const giftItems = store.cart?.items?.filter(item => isFreeGiftItem(item));
    for (const giftItem of giftItems) {
      await cartMethods.change({ id: giftItem.key, quantity: 0 });
    }
    return;
  }
  if (isUpdatingGifts) return;
  isUpdatingGifts = true;

  const thresholds = window.BPN.cart.thresholds;
  const cartItems = store.cart?.items || [];

  // Find all active free gift variant IDs from thresholds
  const activeGiftVariantIds = thresholds
    .filter(t => t.freeGiftProduct)
    .map(t => t.freeGiftProduct.id);

  // If there are no active free gift thresholds, remove all free gifts from cart
  if (activeGiftVariantIds.length === 0) {
    const giftItems = cartItems.filter(item => isFreeGiftItem(item));
    for (const giftItem of giftItems) {
      await cartMethods.change({ id: giftItem.key, quantity: 0 });
    }
    if (giftItems.length > 0) queueLoadCart(true);
    isUpdatingGifts = false;
    return;
  }

  // Use subtotal excluding free gifts just like previous watcher
  const subtotalExcludingGifts = cartItems
    .filter(item => !item.properties || !isFreeGiftItem(item))
    .reduce((total, item) => total + item.discounted_price * item.quantity, 0);

  for (const threshold of thresholds) {
    if (!threshold.freeGiftProduct) continue;
    const giftVariantId = threshold.freeGiftProduct.id;
    const thresholdMet = subtotalExcludingGifts >= threshold.amount;
    const giftItems = cartItems.filter(item => isGiftItem(item, giftVariantId));
    const giftInCart = giftItems.length > 0;

    if (thresholdMet) {
      if (!giftInCart) {
        // Not in cart, add with quantity 1
        await addFreeGiftToCart(giftVariantId);
      } else {
        // In cart, ensure only 1
        for (const giftItem of giftItems) {
          if (giftItem.quantity !== 1) {
            await cartMethods.change({ id: giftItem.key, quantity: 1 });
            queueLoadCart(true);
          }
        }
      }
    } else if (giftInCart) {
      // Remove all free gifts if threshold not met
      for (const giftItem of giftItems) {
        await cartMethods.change({ id: giftItem.key, quantity: 0 });
        queueLoadCart(true);
      }
    }
  }
  // store.isLoading = false;
  isUpdatingGifts = false;
}

// Add this method to handle checkout button click
const onCheckoutClick = async (event) => {
  event.preventDefault();
  // await syncFreeGiftsWithCart();
  await syncBFCMGift()
  // await flushLoadCart();
  // Find the form and submit it programmatically
  const form = event.target.closest('form');
  if (form) form.submit();
};

function prepareLineItemRecommendaitions(recommendationsList, cartItems) {
  const filtered = (recommendationsList || [])
    .filter(rec => !cartItems.some(cartItem => cartItem.product_title === rec.title))
    .map((rec) => {
      if (!rec.selectedVariant && Array.isArray(rec.variants) && rec.variants[0]) {
        rec.selectedVariant = rec.variants[0];
        rec.selectedVariantId = rec.selectedVariant.id;
      }
      return rec;
    });
  return filtered;
}

function applyRecommendations(product, recommendationsList, interstitialData) {
  const recommendations = prepareLineItemRecommendaitions(recommendationsList, store.cart.items);
  product.recommendations = [...recommendations];
  product.interStitialData = { ...interstitialData };
  showCompactUpsells.value = recommendations.length < 1;
}

const checkLineItemRecommendations = async (product) => {
  try {
    if (!window.BPN.interstitial?.cart_recommendations) {
      showCompactUpsells.value = true;
      return;
    }
    const cacheKey = `interstitial_data_${product.product_id}`;
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      const cachedData = JSON.parse(cached);
      applyRecommendations(product, cachedData.recommendations, cachedData.interStitialData);
      return;
    }

    // Fetch new recommendations
    let recommendations = await loadInterstitialRecommendations(product)
    applyRecommendations(product, recommendations, recommendations.find(p => p.isCurrentProduct));
    // Cache the recommendations data
    const cacheData = {
      recommendations: recommendations,
      product_id: product.product_id,
      interStitialData: product.interStitialData
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error loading interstitial data:', error);
  }
};

const loadInterstitialRecommendations = async (product) => {
  const { graphqlClient } = await import('../../utils/graphql-client');
  const data = await graphqlClient.query(PRODUCT_WITH_META_FIELDS_QUERY,
    { handle: product.handle, country: window.Shopify.country || 'US', }
  )
  let transformedData = transformProductResponse(data)
  let recommendations = productRecommendations(transformedData)
  return recommendations;
};

async function syncBFCMGift() {
  store.isLoading = true;
  if (itemsSubtotalPrice.value < 9900 || window?.theme?.bfcm?.saleActive !== true) {
    const giftItems = store.cart?.items?.filter(item => isFreeGiftItem(item));
    if (giftItems && giftItems.length) {
      for (const giftItem of giftItems) {
        await cartMethods.change({ id: giftItem.key, quantity: 0 });
        queueLoadCart();
      }
    }
  } else if (itemsSubtotalPrice.value >= 9900 && window?.theme?.bfcm?.saleActive === true) {
    const giftVariantId = window?.theme?.bfcm?.freeGift?.variantId;
    const giftItems = store.cart?.items?.filter(item => isGiftItem(item, giftVariantId));
    if (!giftItems || giftItems.length === 0) {
      await addFreeGiftToCart(giftVariantId);
    }
  }
}
</script>
